<template>
    <div v-if="!isProductsEmpty"
        class="my-2xl h-full bg-gray-50 px-sm md:px-0">
        <v-container class="space-y-lg py-lg">
            <v-h3>{{ t('related-products') }}</v-h3>

            <v-product-recommendations
                v-slot="{ isLoading, products }"
                :name="inSlider ? undefined : content?.recommendation_id"
                @no-products="isProductsEmpty = true">
                <nuxt-link :to="url"
                    class="relative grid bg-[color:var(--background-color)] text-[color:var(--text-color)]
                        md:grid-cols-6 md:rounded"
                    :class="{ 'h-full ': inWideSlider}"
                    @click="addPromotionClick(promo)">
                </nuxt-link>

                <div v-if="(!inSlider || !inWideSlider) && (isLoading || products.length > 0)"
                    class="relative z-10">
                    <div class="absolute inset-x-0 top-[-40px] h-[120px]
                        bg-gradient-to-b from-transparent
                        via-[color:var(--background-color)] to-[color:var(--background-color)]">
                    </div>

                    <div class="absolute inset-x-0 top-[40px] h-[20px] bg-[color:var(--background-color)] md:h-[110px]
                        md:rounded-b">
                    </div>

                    <v-scrollable class="flex snap-x scroll-px-sm flex-nowrap space-x-xs
                        px-sm md:scroll-px-lg md:space-x-sm md:px-lg">
                        <template v-if="isLoading">
                            <v-scrollable-item v-for="i in 10"
                                :key="i"
                                class="last-child:mr-lg mb-auto w-[160px] shrink-0 snap-start md:w-[260px]">
                                <v-product-card-placeholder />
                            </v-scrollable-item>
                        </template>

                        <v-scrollable-item v-for="product in products"
                            v-else
                            :key="product.key"
                            class="w-[160px] shrink-0 snap-start md:w-[260px]"
                        >
                            <v-elevate-product-card :product="product" />
                        </v-scrollable-item>
                    </v-scrollable>
                </div>
            </v-product-recommendations>
        </v-container>
    </div>
</template>

<script setup lang="ts">
import type { ISbStoryData } from 'storyblok-js-client';
import { type ProductRecommendationsContent } from '~~/types';

const properties = defineProps<{
    story: ISbStoryData
    content?: ProductRecommendationsContent
}>();

const { addPromotionClick } = usePromotion();
const { format: formatSbUrl } = useSbUrl();

/**
 * Problem: We need to figure out how to use article page path for fetching product
 * recommendations from Elevate. At current state we can't use the path now as is if we don't
 * corresponding path set in Elevate for article page.
 */

const blokData = computed(() => properties.story.content);

const inWideSlider = inject('sb.inWideSlider', false);
const inSlider = inject('sb.inSlider', false);

const url = computed(() => formatSbUrl(blokData.value.title));

function convertString(string: string) {
    const words = string.split('-');
    const convertedString = words.map(((word) => word.charAt(0).toUpperCase() + word.slice(1))).join(' ');
    return convertedString;
}

const { t } = useI18n();

const route = useRoute();

const promo = computed(() => ({
    /* eslint-disable @typescript-eslint/naming-convention */
    creative_name: convertString(blokData?.value?.component ?? '') ?? 'Promotion',
    creative_slot: `${blokData.value.component} / 0`, // component name / index
    // eslint-disable-next-line no-underscore-dangle
    promotion_id: blokData.value._uid ?? '',
    promotion_name: blokData.value.title,
    source_url: route.fullPath,
    target_url: url.value,
    /* eslint-enable @typescript-eslint/naming-convention */
}));

const isProductsEmpty = ref<boolean>(false);
</script>

<style scoped>

</style>

<i18n lang="json">
    {
        "et": {
            "related-products": "Postitusega seotud tooted"
        },
        "fi": {
            "related-products": "Artikkeliin liittyviä tuotteita"
        }
    }
</i18n>
